import React, { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { theme } from '../theme'
import { Box, Stack, Typography, Divider, Button, CircularProgress } from '@mui/material'
import { StyledBox, DiscountLabel, BrandName, StockLabel } from '../components/styledComponents/StyledItemComponents'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import ImageSlider from '../components/ImageSlider/ImageSlider'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { Currency } from 'react-intl-number-format'
import { useAppContext } from '../context/AppProvider'
import { useItems } from '../hooks/useItems'
import { useFavorites } from '../hooks/useFavorites'
import { useCart } from '../hooks/useCart'
import { debounce } from 'lodash'
import { SHIPPING_PRICE, getCollectionBadge, getDisplayBrandName, getEffectivePrice, getStockLabel } from '../utils/itemPresentation'

const getFavoriteButtonSx = (active) => ({
  borderRadius: '21px',
  textTransform: 'none',
  borderColor: theme.palette.accent.main,
  color: active ? '#FFFFFF' : theme.palette.accent.main,
  backgroundColor: active ? theme.palette.accent.main : 'transparent',
  '&:hover': {
    borderColor: theme.palette.accent.light,
    backgroundColor: active ? theme.palette.accent.light : 'rgba(255, 0, 21, 0.08)'
  }
})

const Item = () => {
  const { state } = useAppContext()
  const { addItemToFavorites, removeItemFromFavorites } = useFavorites()
  const { updateCartItem } = useCart()
  const { itemId } = useParams()
  const { fetchItemsById } = useItems()

  const [item, setItem] = useState({ loading: false, data: {}, error: null })
  const isItemInFavorites = state.favorites.results?.some(favoriteItem => favoriteItem._id === item.data._id)
  const [addingToCart, setAddingToCart] = useState(false)

  useEffect(() => {
    fetchItemsById(itemId).then((payload) => {
      setItem({ data: payload.data, loading: false, error: payload.error })
    })
  }, [itemId, fetchItemsById])

  const debouncedCartAction = useCallback(debounce((action, data) => {
    action(data)
    setAddingToCart(false)
  }, 1000, {
    leading: false,
    trailing: true
  }), [])

  const handleAddToCart = () => {
    setAddingToCart(true)
    debouncedCartAction(updateCartItem, item)
  }

  const debouncedFavoriteAction = useCallback(debounce((action, data) => {
    action(data)
  }, 100, {
    leading: false,
    trailing: true
  }), [])

  const handleAddToFavorites = () => {
    const action = isItemInFavorites ? removeItemFromFavorites : addItemToFavorites
    debouncedFavoriteAction(action, item)
  }

  const collectionBadge = getCollectionBadge(item.data)
  const stockLabel = getStockLabel(item.data)
  const finalPrice = getEffectivePrice(item.data)
  const displayBrandName = getDisplayBrandName(item.data)

  return (
    <Box sx={{ backgroundColor: theme.palette.secondary.main, padding: '40px 0' }}>
      {!item.loading &&
        <StyledBox>
          <Stack direction={{ xs: 'column', sm: 'row', md: 'row' }} spacing={2} justifyContent='space-between'>
            <Box
              bgcolor={theme.palette.primary.main}
              flex={2}
              p='30px'
              border='solid 1px'
              borderColor={theme.palette.borderColor.main}
              borderRadius='4px'
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden', height: '100%', maxHeight: '70vh' }}
            >
              <ImageSlider images={item.data.images} sx={{ width: 'auto', maxWidth: '100%' }} />
            </Box>
            <Box bgcolor={theme.palette.primary.main} flex={3} p='30px 40px' border='solid 1px' borderColor={theme.palette.borderColor.main} borderRadius='4px'>
              {collectionBadge && (
                <Box sx={{ marginBottom: '12px' }}>
                  <DiscountLabel sx={{ top: 0, left: 0 }}>
                    {collectionBadge}
                  </DiscountLabel>
                </Box>
              )}
              <Typography variant='h1' fontSize='18px' fontWeight={600} paddingBottom='18px'>{item.data.name}</Typography>
              {item.data.brand?.logo
                ? (
                  <Box sx={{ marginBottom: '18px' }}>
                    <Box
                      component='img'
                      src={item.data.brand.logo}
                      alt={item.data.brand?.name || 'Brand logo'}
                      sx={{ maxHeight: 34, width: 'auto', objectFit: 'contain' }}
                    />
                  </Box>
                  )
                : displayBrandName ? <BrandName sx={{ marginBottom: '18px' }}>{displayBrandName}</BrandName> : null}
              <Box>
                <Typography variant='h2' fontSize='16px' fontWeight={500} paddingBottom='16px'>Descriere</Typography>
                <Typography variant='span' fontSize='16px'>
                  {item.data.description && item.data.description.map((description, index) => (
                    <span key={index}>
                      {description}
                      {index !== item.data.description.length - 1 && <br />}
                    </span>
                  ))}
                </Typography>
              </Box>
              <Divider orientation='horizontal' aria-hidden='true' color={theme.palette.fontColor.main}
                sx={{ margin: '20px 0' }}/>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 2 }}>
                <Box>
                  <StockLabel sx={{ position: 'relative', left: '0px', paddingLeft: '10px', marginBottom: '12px' }}>
                    {stockLabel}
                  </StockLabel>
                  <Typography variant='h6' sx={{ color: theme.palette.accent.main, fontSize: '22px', fontWeight: 600 }}>
                    <Currency locale='ro-RO' currency='RON'>
                      {finalPrice}
                    </Currency>
                  </Typography>
                  <Typography variant='body1' sx={{ color: theme.palette.fontColor.medium, marginTop: '8px' }}>
                    Transport: {SHIPPING_PRICE} lei
                  </Typography>
                </Box>
                <Box sx={{ marginTop: '10px', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    aria-label='favorite'
                    onClick={handleAddToFavorites}
                    variant={isItemInFavorites ? 'contained' : 'outlined'}
                    startIcon={isItemInFavorites ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    sx={getFavoriteButtonSx(isItemInFavorites)}
                  >
                    Favorite
                  </Button>
                  <Button
                    onClick={handleAddToCart}
                    variant='contained'
                    size='medium'
                    disabled={addingToCart}
                    startIcon={addingToCart ? <CircularProgress size={22} /> : <ShoppingCartIcon />}
                    sx={{
                      color: 'white',
                      borderRadius: '21px',
                      textTransform: 'none',
                      backgroundColor: '#6f6f6f',
                      '&:hover': { backgroundColor: '#5f5f5f' }
                    }}
                  >
                    {addingToCart ? 'Adăugare...' : 'ADAUGĂ'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Stack>
        </StyledBox>
      }
    </Box>
  )
}

export default Item
